import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { connect, useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { createSelector } from 'reselect';

import { Urls, ModalTypes, ReportTypes } from 'constants/constants';
import { formatHours as fm, formatNumber as fn, formatPercent as fp, waitAtLeast } from 'utils/utils';
import { useUpdateEffect, usePolling } from 'utils/components';

import Account, { selectCurrentAccount, getChannelIdsByTag } from 'store/models/Account';
import Channel, { selectChannels, twitchUrl } from 'store/models/Channel';
import Brand, { selectBrands } from 'store/models/Brand';
import Stats, { selectAccountStats, selectChannelStats, selectOverlayStats } from 'store/models/Stats';
import Stream, { selectStreams, isStreamLive } from 'store/models/Stream';
import Session, { selectSessions, isSessionRunning } from 'store/models/Session';
import { selectCurrentUser } from 'store/models/User';
import RouteState, { currentPath } from 'store/RouteState';
import UIState from 'store/UIState';
import AppState, { selectAppDateRange } from 'store/AppState';

import { ContentCenter, Title, Header, HeaderLeft, HeaderRight, Breadcrumbs } from 'components/Content';
import { AddButton, Button, DownloadIconButton, CircleAddButton, RefreshIconButton } from 'components/Button';
import { ChannelList } from 'components/ChannelItem';
import { StatBox, StatsHeader } from 'components/Stats';
import Tooltip from 'components/Tooltip';
import DateRange from 'components/DateRange';
import EmptyItem from 'components/EmptyItem';
import { AllTagsFilter, TagFilter, FilterList } from 'components/Filter';



// ======================
// STORE
// ======================
const selectors = createSelector(
  selectCurrentUser,
  selectCurrentAccount,
  selectChannels,
  selectBrands,
  selectAccountStats,
  selectChannelStats,
  selectStreams,
  selectSessions,
  selectAppDateRange,
  (user, account, channels, brands, stats, channelStats, streams, sessions, dateRange,) => {
    let liveStreams = streams 
      |> _.filter(#, isStreamLive)
      |> _.keyBy(#, 'channelId');
    let liveSessions = sessions |> _.keyBy(#, 'channelId');
    channels = _.orderBy(channels, c => {
      let stream = liveStreams?.[c._id];
      let session = liveSessions?.[c._id];
      return isStreamLive(stream)
        ? 10000000000
        : isSessionRunning(session) 
          ? 90000 
          : channelStats?.[c._id]?.sceneViews ?? 0;
    }, 'desc');
    return {
      user,
      account,
      channels,
      brands,
      stats,
      channelStats,
      channelSettings: account?.channels 
        |> _.keyBy(#, 'channelId') 
        |> _.mapValues(#, 'settings'),
      liveStreams,
      liveSessions,
      dateRange,
    }
  },
);


// ======================
// COMPONENT
// ======================
const ChannelsScreen = ({}) => {
  const dispatch = useDispatch();
  const { dateRange, account, channels, brands, stats, channelSettings, channelStats, liveStreams, liveSessions, overlayStats } = useSelector(selectors);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedTag, setSelectedTag] = useState();
  const hasChannels = _.size(channels) !== 0;
  const hasBrands = _.size(brands) !== 0;
  const tags = account?.tags;
  const channelsByTag = getChannelIdsByTag(account);
  const hasFilter = !_.isNil(selectedTag);
  const fileteredChannels = hasFilter
    ? channels |> _.filter(#, channel => _.includes(channelsByTag[selectedTag], channel._id))
    : channels;

  const loadTimer = useRef();
  const POLLING_LENGTH = 60000;

  
  
  useEffect(() => {
    onLoadPageData();
    return () => {
      if(loadTimer.current)
        clearTimeout(loadTimer.current);
    }
  }, []);

  useUpdateEffect(() => {
    fetchStats();
  }, [selectedTag])

  useUpdateEffect(() => {
    fetchStats();
  }, [dateRange])

  const fetchData = async (setStateFn) => {
    let {from,to} = dateRange.dates;
    setStateFn(true);
    waitAtLeast(async () => {
      await dispatch(Channel.actions.fetchChannels());
      await dispatch(Brand.actions.fetchBrands());
      await dispatch(Stream.actions.fetchLiveStreams());
      await dispatch(Session.actions.fetchRunningSessions());
      await dispatch(Stats.actions.fetchAccountStats(from.valueOf(), to.valueOf(), selectedTag));
      await dispatch(Stats.actions.fetchChannelStats(from.valueOf(), to.valueOf(), selectedTag));
    }, () => {
      setStateFn(false);
    }, loadTimer, 500);
  };

  const onPolling = async () => {
    if(exporting || refreshing || loading)
      return;
    await fetchData(setRefreshing);
  };
  const [isPolling, stopPolling, resetPolling] = usePolling(onPolling, POLLING_LENGTH);
  
  const onLoadPageData = async () => {
    await fetchData(setLoading);
    resetPolling();
  }

  const fetchStats = async () => {
    let {from,to} = dateRange.dates;
    setLoading(true);
    await dispatch(Stats.actions.fetchAccountStats(from.valueOf(), to.valueOf(), selectedTag));
    await dispatch(Stats.actions.fetchChannelStats(from.valueOf(), to.valueOf(), selectedTag));
    setLoading(false);
  };


  const onExportView = () => dispatch(UIState.actions.showModal(ModalTypes.EXPORT));
  const onExportChannelData = (channelId) => dispatch(UIState.actions.showModal(ModalTypes.EXPORT, { report: ReportTypes.CHANNEL, channelId }));
  const onAddChannel = () => dispatch(UIState.actions.showModal( ModalTypes.ADD_CHANNEL));
  const onDeleteChannel = channelId => dispatch(Channel.actions.removeChannel(channelId));
  const onVisitBrands = () => dispatch(RouteState.actions.navigate(Urls.BRANDS));
  const onGetAssets = (channelId) => dispatch(RouteState.actions.navigate(Urls.CHANNEL_SETTINGS_ROUTE, { channelId }));

  const onSessionCheck = channelId =>
    dispatch(RouteState.actions.navigate(Urls.SLIDESHOW_EDIT_ROUTE, { channelId }));
  //this.props.showHealthCheckModal(this.props.overlay?._id, channelId)
  const onTwitchView = (channelId, channel) => window.open(twitchUrl(channel))
  const onSlideshow = channelId => dispatch(RouteState.actions.navigate(Urls.SLIDESHOW_EDIT_ROUTE, { channelId }, null, { back: currentPath() }));

  const onFilterSelect = tagId => setSelectedTag(tagId)

  return (
    <ContentCenter className="channels">
      <Header split={true}>
        <HeaderLeft>
          <Title>
            Channels 
            <Tooltip tooltip="Add Channel">
              <CircleAddButton size="s" onClick={onAddChannel} />
            </Tooltip>
          </Title>
        </HeaderLeft>
        <HeaderRight>
          <DateRange disabled={loading}  />
          <DownloadIconButton color="light" disabled={loading || refreshing || exporting} onClick={onExportView} />
          <RefreshIconButton color="light" onClick={onLoadPageData} loading={refreshing || exporting} disabled={refreshing || exporting || loading} />
        </HeaderRight>
      </Header>
      { !_.isEmpty(tags) && 
      <FilterList>
        <AllTagsFilter onSelect={onFilterSelect} selected={_.isNil(selectedTag)}>All</AllTagsFilter>
        { _.map(tags, tag => <TagFilter key={tag.tagId} onSelect={onFilterSelect} filterId={tag.tagId} selected={selectedTag === tag.tagId}>{tag.name}</TagFilter>)}
      </FilterList>}
      <StatsHeader>
        <StatBox
          label="Media Value"
          value={stats && `$${fn(stats?.totalMediaValue)}`}
          loading={loading} />
        <StatBox
          label="Impressions"
          value={stats && fn(stats?.sceneViews)}
          loading={loading} />
        <StatBox
          label="Clicks"
          value={fn(stats?.clicks)}
          loading={loading} />
        <StatBox
          label="Streaming"
          value={stats && fm(stats?.streamMinutes, 0)}
          loading={loading} />
      </StatsHeader>

      { !hasChannels && !loading &&
      <EmptyItem className="no-channels" size="xl">
        <h1>Welcome To HappyStreams!</h1>
        <h2><strong>Step 1:</strong> Start By Adding Twitch or YouTube Channels To Your Account</h2>
        <AddButton onClick={onAddChannel} size="l">Add Channels</AddButton>
      </EmptyItem>}

      <ChannelList 
        loading={loading}
        channels={fileteredChannels} 
        channelStats={channelStats}
        channelSettings={channelSettings}
        streams={liveStreams}
        sessions={liveSessions}
        onExport={onExportChannelData}
        onGetAssets={onGetAssets}
        onSlideshow={onSlideshow}
        onSessionCheck={onSessionCheck}
        onTwitchView={onTwitchView}
        onDelete={onDeleteChannel} />

      { hasChannels && hasFilter && _.isEmpty(fileteredChannels) && !loading &&
      <EmptyItem className="no-filtered" size="xl">
        <p>There are no channels grouped with this tag</p>
      </EmptyItem>} 

      { hasChannels && !hasBrands && !loading &&
      <EmptyItem className="no-brands" size="xl">
        <h1>Step 2: Create Brands</h1>
        <h2>Create a brand for each sponsor you want to display on your streams</h2>
        <AddButton onClick={onVisitBrands} size="l">Visit Brand Screen</AddButton>
      </EmptyItem>}
    </ContentCenter>)
}
export default ChannelsScreen;
